import { IonIcon } from '@ionic/react';
import { enterOutline, exitOutline, home, stopwatch } from 'ionicons/icons';
import { ReactElement, ReactNode } from 'react';
import {
  FlowItemAction,
  FlowItemBase,
} from '../../../../shared/component/flow-item/view/flow-item-base';
import '../../../../shared/component/flow-item/view/flow-item.css';
import { moveItem } from '../../../../shared/component/icon/move-item';
import { reportProduction } from '../../../../shared/component/icon/report-production';
import { waitForOperator } from '../../../../shared/component/icon/wait-for-operator';
import { ExecutionInfo } from '../../../../shared/hook/live-execution.hooks';
import {
  FlowItem,
  FlowItemParameter,
  FlowItemRef,
  SystemUnit,
  WaitForInputType,
} from '../../../../shared/model/flow.types';
import { BeaconWeldingData } from '../../model/beacon-welding-data';
import { linearWeld } from '../icon/linear-weld';
import { radialWeld } from '../icon/radial-weld';
import { stitchWeld } from '../icon/stitch-weld';
import { tackWeld } from '../icon/tack-weld';
import { weldItem } from '../icon/weld-item';

export interface FlowItemProps {
  flowItem: FlowItem;
  isItemSelected?: boolean;
  isSectionSelected?: boolean;
  canAddItem?: boolean;
  canReorderItem: boolean;
  onAddItemsBefore: () => void;
  onAddItemsAfter: () => void;
  onSelectItem?: () => void;
  actions?: FlowItemAction[];
  highlight?: boolean;
  system: SystemUnit;
  executionInfo?: ExecutionInfo;
  isStartItem?: boolean;
  isInactive?: boolean;
  onLongPress?: (e: Event, id: string) => void;
  presentItemQuickActions?: (e: Event, id: string) => void;
  label?: string;
  addPrecedingStem?: boolean;
}

const WeldingFlowItemComponent = (props: FlowItemProps): ReactElement => {
  const {
    actions,
    flowItem,
    isItemSelected,
    isSectionSelected,
    canAddItem,
    canReorderItem,
    highlight,
    onAddItemsBefore,
    onAddItemsAfter,
    onSelectItem,
    system,
    executionInfo,
    isStartItem,
    isInactive,
    onLongPress,
    label,
    presentItemQuickActions,
    addPrecedingStem,
  } = props;

  const icon = getFlowItemIconFromItem(flowItem);

  return (
    <FlowItemBase
      flowItem={flowItem}
      canAddItem={canAddItem}
      canReorderItem={canReorderItem}
      isItemSelected={isItemSelected}
      isSectionSelected={isSectionSelected}
      highlight={highlight}
      onAddItemsBefore={onAddItemsBefore}
      onAddItemsAfter={onAddItemsAfter}
      onSelectItem={onSelectItem}
      icon={icon}
      actions={actions}
      system={system}
      executionInfo={executionInfo}
      isStartItem={isStartItem}
      isInactive={isInactive}
      onLongPress={onLongPress}
      label={label}
      presentItemQuickActions={presentItemQuickActions}
      addPrecedingStem={addPrecedingStem}
    />
  );
};

const getFlowItemIconFromItem = (flowItem: FlowItem): ReactNode => {
  const weldType = flowItem.parameters.find(
    (p) => p.ref === BeaconWeldingData.FlowItemParameterRef.WELD_TYPE
  );
  const waitForType = getWaitForType(flowItem);
  return getFlowItemIcon(flowItem.ref, weldType?.value, waitForType?.value);
};

export const getWaitForType = (
  flowItem: FlowItem
): FlowItemParameter<BeaconWeldingData.WaitForInputType> | undefined => {
  return flowItem.parameters.find(
    (p) =>
      p.ref === BeaconWeldingData.FlowItemParameterRef.WAIT_FOR_TYPE_OF_INPUT
  );
};

export const getFlowItemIcon = (
  flowItemRef: FlowItemRef,
  weldType?: BeaconWeldingData.WeldTypeEnum,
  waitForType?: BeaconWeldingData.WaitForInputType
): ReactNode => {
  let icon: ReactNode = <div>?</div>;
  const classList: string[] = ['flow-item-icon', 'full-hw', 'center'];
  if (flowItemRef === BeaconWeldingData.FlowItemRef.WELD) {
    const weldClassList: string[] = [...classList, 'bg-danger'];

    let weldTypeIcon = weldItem;
    if (weldType === BeaconWeldingData.WeldTypeEnum.LINEAR) {
      weldTypeIcon = linearWeld;
    } else if (weldType === BeaconWeldingData.WeldTypeEnum.RADIAL) {
      weldTypeIcon = radialWeld;
    } else if (weldType === BeaconWeldingData.WeldTypeEnum.LINEAR_STITCH) {
      weldTypeIcon = stitchWeld;
    } else if (weldType === BeaconWeldingData.WeldTypeEnum.TACK) {
      weldTypeIcon = tackWeld;
      weldClassList.push('ringed-icon');
    } else if (weldType === BeaconWeldingData.WeldTypeEnum.ROTARY) {
      // TODO: do we need another icon?
      weldTypeIcon = radialWeld;
      weldClassList.push('ringed-icon');
    }

    icon = (
      <div className={weldClassList.join(' ')}>
        <IonIcon icon={weldTypeIcon} size="large" />
      </div>
    );
  } else if (flowItemRef === BeaconWeldingData.FlowItemRef.MOVE) {
    icon = <IonIcon icon={moveItem} size="large" />;
  } else if (flowItemRef === BeaconWeldingData.FlowItemRef.WAIT_FOR) {
    if (waitForType === WaitForInputType.TIME) {
      icon = (
        <div className={[...classList, 'bg-alt'].join(' ')}>
          <IonIcon icon={stopwatch} size="large" />
        </div>
      );
    } else if (waitForType === WaitForInputType.SENSOR) {
      icon = (
        <div className={[...classList, 'bg-alt'].join(' ')}>
          <IonIcon icon={enterOutline} size="large" />
        </div>
      );
    } else {
      icon = (
        <div className={[...classList, 'bg-alt'].join(' ')}>
          <IonIcon icon={waitForOperator} size="large" />
        </div>
      );
    }
  } else if (
    flowItemRef === BeaconWeldingData.FlowItemRef.PRODUCTION_REPORTING
  ) {
    icon = (
      <div className={[...classList, 'bg-success'].join(' ')}>
        <IonIcon icon={reportProduction} size="large" />
      </div>
    );
  } else if (flowItemRef === BeaconWeldingData.FlowItemRef.MOVE_HOME) {
    icon = (
      <div className={classList.join(' ')}>
        <IonIcon icon={home} size="large" />
      </div>
    );
  } else if (flowItemRef === BeaconWeldingData.FlowItemRef.TRIGGER_OUTPUT) {
    icon = (
      <div className={[...classList, 'bg-alt'].join(' ')}>
        <IonIcon icon={exitOutline} size="large" />
      </div>
    );
  }

  return icon;
};

export { WeldingFlowItemComponent };
